/**
 * useHybridFlow - Hook for the step-based hybrid flow engine
 *
 * A deterministic step-based flow with optional dynamic content.
 * Progress is saved via onProgressUpdate callback for persistence.
 */

import { useState, useCallback, useRef, useEffect } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";
import type {
  Layout,
  Action,
  HybridFlowRequest,
  HybridFlowResponse,
} from "@um/contracts";
import type { OnboardingProgress } from "@um/contracts";

/**
 * Keys that are needed in collectedData for the backend flow engine
 * but should NOT be persisted to Firestore for crash recovery.
 *
 * These are either:
 * - Backend round-trip state (medication loop, mind lists)
 * - Cleared at the source after use but kept here as a safety net
 */
const NON_PERSISTABLE_KEYS = new Set([
  "imageBase64",             // cleared after character creation; safety net for the Firestore write
  "reactionMessage",         // cleared after character creation; safety net
  "existingMinds",           // re-fetched on load; backend needs it in collectedData but not in Firestore
  "_lastExtraction",         // backend medication loop state; not useful after a refresh
  "_extractionIterations",   // backend medication iteration cards; not useful after a refresh
  "_nextInputLabel",         // backend medication dynamic label
  "_needsRefinement",        // backend medication loop flag
  "_stayOnReviewCard",
  "_supportPendingScheduleText",
  "_supportInstructionsAsked",
  "_supportScheduleRaw",
  "_supportScheduleFollowUp",
]);

/**
 * Sanitize collectedData before persisting to Firestore.
 * - Strips keys in NON_PERSISTABLE_KEYS
 * - Removes undefined values (Firestore rejects them)
 */
function sanitizeForPersistence(
  data: Record<string, unknown>
): Record<string, unknown> {
  const clean: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(data)) {
    if (NON_PERSISTABLE_KEYS.has(key)) continue;
    if (value === undefined) continue;
    clean[key] = value;
  }
  return clean;
}

interface UseHybridFlowOptions {
  flowType: string;
  accountId: string;
  onComplete?: (data: Record<string, unknown>) => void | Promise<void>;
  onProgressUpdate?: (progress: OnboardingProgress) => Promise<void>;
  initialProgress?: OnboardingProgress | null;
}

interface HybridFlowState {
  flowType: string;
  stepIndex: number;
  totalSteps: number;
  stepId: string;
  message: string;
  loadingMessage: string | undefined;
  layout: Layout;
  action: Action | null;
  inputRequired: boolean;
  speakerName: string | undefined;
  speakerImageUrl: string | undefined;
  characterPanel: string | undefined;
  characterPanelCycle: string[] | undefined;
  collectedData: Record<string, unknown>;
  isComplete: boolean;
  isLoading: boolean;
  error: string | null;
  pendingHandoff: string | null;
  autoAdvance: boolean | undefined;
}

const callStepFlow = httpsCallable<HybridFlowRequest, HybridFlowResponse>(
  functions,
  "stepFlow",
  { timeout: 180000 } // 3 minutes
);

export function useHybridFlow({
  flowType: initialFlowType,
  accountId,
  onComplete,
  onProgressUpdate,
  initialProgress,
}: UseHybridFlowOptions) {
  // Track whether the flow has started (to know when to stop updating initialProgressRef)
  const hasStartedRef = useRef(false);

  // Capture initial progress - update it when initialProgress changes, but only before flow starts
  const initialProgressRef = useRef(initialProgress);

  // Update initialProgressRef when initialProgress changes (but only before start() is called)
  // This handles the case where settings loads after the first render
  if (
    !hasStartedRef.current &&
    initialProgress !== initialProgressRef.current
  ) {
    initialProgressRef.current = initialProgress;
  }

  const [state, setState] = useState<HybridFlowState>(() => ({
    flowType: initialFlowType,
    stepIndex: initialProgressRef.current?.stepIndex ?? 0,
    totalSteps: 0,
    stepId: "",
    message: "",
    loadingMessage: undefined,
    layout: "full",
    action: null,
    inputRequired: false,
    speakerName: undefined,
    speakerImageUrl: undefined,
    characterPanel: undefined,
    characterPanelCycle: undefined,
    collectedData: initialProgressRef.current?.collectedData ?? {},
    isComplete: false,
    isLoading: false,
    error: null,
    pendingHandoff: null,
    autoAdvance: undefined,
  }));

  // History stack for back navigation
  const historyRef = useRef<HybridFlowState[]>([]);

  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const onProgressUpdateRef = useRef(onProgressUpdate);
  onProgressUpdateRef.current = onProgressUpdate;

  /**
   * Update state from backend response
   */
  const updateStateFromResponse = useCallback(
    (data: HybridFlowResponse, flowType: string, saveHistory = true) => {
      setState((prev) => {
        // Save current state to history before updating (for back navigation)
        if (saveHistory && prev.stepId) {
          historyRef.current.push({ ...prev });
        }

        return {
          ...prev,
          stepIndex: data.stepIndex,
          totalSteps: data.totalSteps,
          stepId: data.stepId,
          message: data.message,
          loadingMessage: data.loadingMessage,
          layout: data.layout,
          action: data.action || null,
          inputRequired: data.inputRequired,
          speakerName: data.speakerName,
          speakerImageUrl: data.speakerImageUrl,
          characterPanel: data.characterPanel,
          characterPanelCycle: data.characterPanelCycle,
          collectedData: data.collectedData,
          isComplete: data.isComplete,
          isLoading: false,
          error: null,
          pendingHandoff: data.handoffTo || null,
          autoAdvance: data.autoAdvance,
        };
      });

      // Save progress after each step (but not when complete - we clear it then)
      // Skip persisting transient steps like "auth-waiting" — that step depends on
      // an open popup that won't exist after a page refresh. By not saving it, a
      // refresh falls back to the previous persisted step ("auth-link") where the
      // user can tap the sign-in buttons again.
      // Fire-and-forget with explicit error handling so Firestore write failures
      // surface in the console instead of becoming silent unhandled rejections.
      if (!data.isComplete && data.stepId !== "auth-waiting" && onProgressUpdateRef.current) {
        onProgressUpdateRef
          .current({
            flowType,
            stepIndex: data.stepIndex,
            collectedData: sanitizeForPersistence(data.collectedData),
            updatedAt: new Date().toISOString(),
          })
          .catch((err: unknown) => {
            console.error("[useHybridFlow] Failed to save progress:", err);
          });
      }

      if (data.isComplete && onCompleteRef.current) {
        onCompleteRef.current(data.collectedData);
      }
    },
    []
  );

  /**
   * Start the flow - resumes from initialProgress if provided, otherwise starts at step 0
   * @param initialData - Optional data to include in collectedData (passed directly to avoid React state timing issues)
   */
  const start = useCallback(
    async (initialData?: Record<string, unknown>) => {
      // Mark that the flow has started - stop updating initialProgressRef after this
      hasStartedRef.current = true;

      // If we have initial progress (captured before start), resume from that step
      // Use the saved flowType if available (handles resuming after handoffs)
      const resumeStepIndex = initialProgressRef.current?.stepIndex ?? 0;
      // Merge saved progress with current state.collectedData and any directly passed initialData
      // Priority: initialData > state.collectedData > saved progress
      const resumeCollectedData = {
        ...(initialProgressRef.current?.collectedData ?? {}),
        ...state.collectedData,
        ...(initialData ?? {}),
      };
      const resumeFlowType =
        initialProgressRef.current?.flowType || state.flowType;

      // Update state with the correct flowType (in case we're resuming a different flow)
      setState((prev) => ({
        ...prev,
        flowType: resumeFlowType,
        isLoading: true,
        error: null,
      }));

      console.log("[useHybridFlow] Starting flow:", {
        initialFlowType: state.flowType,
        resumeFlowType,
        resumeStepIndex,
        hasInitialProgress: !!initialProgressRef.current,
        collectedDataKeys: Object.keys(resumeCollectedData),
      });

      try {
        const result = await callStepFlow({
          flowType: resumeFlowType,
          stepIndex: resumeStepIndex,
          accountId,
          collectedData: resumeCollectedData,
        });

        const data = result.data;

        if (!data.success) {
          setState((prev) => ({
            ...prev,
            isLoading: false,
            error: data.error?.message || "Failed to start flow",
          }));
          return;
        }

        updateStateFromResponse(data, resumeFlowType);
      } catch (error) {
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error:
            error instanceof Error ? error.message : "Failed to start flow",
        }));
      }
    },
    [state.flowType, state.collectedData, accountId, updateStateFromResponse]
  );

  /**
   * Switch to a different flow (for handoffs)
   */
  const switchFlow = useCallback(
    async (newFlowType: string, carryOverData?: Record<string, unknown>) => {
      setState((prev) => ({
        ...prev,
        flowType: newFlowType,
        stepIndex: 0,
        collectedData: carryOverData || {},
        isLoading: true,
        error: null,
        pendingHandoff: null,
      }));

      try {
        const result = await callStepFlow({
          flowType: newFlowType,
          stepIndex: 0,
          accountId,
          collectedData: carryOverData || {},
        });

        const data = result.data;

        if (!data.success) {
          setState((prev) => ({
            ...prev,
            isLoading: false,
            error: data.error?.message || "Failed to switch flow",
          }));
          return;
        }

        setState((prev) => ({
          ...prev,
          flowType: newFlowType,
          stepIndex: data.stepIndex,
          totalSteps: data.totalSteps,
          stepId: data.stepId,
          message: data.message,
          loadingMessage: data.loadingMessage,
          layout: data.layout,
          action: data.action || null,
          inputRequired: data.inputRequired,
          speakerName: data.speakerName,
          speakerImageUrl: data.speakerImageUrl,
          characterPanel: data.characterPanel,
          characterPanelCycle: data.characterPanelCycle,
          collectedData: data.collectedData,
          isComplete: data.isComplete,
          isLoading: false,
          error: null,
          pendingHandoff: data.handoffTo || null,
          autoAdvance: data.autoAdvance,
        }));

        // Persist progress for the NEW flow immediately. Without this, the
        // persisted progress keeps pointing at the previous flow's last step
        // until the user completes a step in the new flow — a reload in that
        // window resumes the OLD flow (e.g. back to onboarding step 0 after
        // the welcome → character-creation handoff).
        if (
          !data.isComplete &&
          data.stepId !== "auth-waiting" &&
          onProgressUpdateRef.current
        ) {
          onProgressUpdateRef
            .current({
              flowType: newFlowType,
              stepIndex: data.stepIndex,
              collectedData: sanitizeForPersistence(data.collectedData),
              updatedAt: new Date().toISOString(),
            })
            .catch((err: unknown) => {
              console.error(
                "[useHybridFlow] Failed to save progress after flow switch:",
                err
              );
            });
        }
      } catch (error) {
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error:
            error instanceof Error ? error.message : "Failed to switch flow",
        }));
      }
    },
    [accountId]
  );

  /**
   * Proceed to the next step
   *
   * - Send CURRENT step index with user input
   * - Backend processes current step, then returns next step
   */
  const next = useCallback(
    async (userMessage?: string, userImageBase64?: string) => {
      // If there's a pending handoff, switch to that flow
      if (state.pendingHandoff) {
        await switchFlow(state.pendingHandoff, state.collectedData);
        return;
      }

      setState((prev) => ({ ...prev, isLoading: true, error: null }));

      try {
        const result = await callStepFlow({
          flowType: state.flowType,
          stepIndex: state.stepIndex,
          accountId,
          collectedData: state.collectedData,
          ...(userMessage ? { userMessage } : {}),
          ...(userImageBase64 ? { userImageBase64 } : {}),
        });

        const data = result.data;

        if (!data.success) {
          setState((prev) => ({
            ...prev,
            isLoading: false,
            error: data.error?.message || "Failed to proceed",
          }));
          return;
        }

        updateStateFromResponse(data, state.flowType);
      } catch (error) {
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: error instanceof Error ? error.message : "Failed to proceed",
        }));
      }
    },
    [
      state.flowType,
      state.stepIndex,
      state.collectedData,
      state.pendingHandoff,
      accountId,
      switchFlow,
      updateStateFromResponse,
    ]
  );

  /**
   * Reset the flow to the beginning
   */
  const reset = useCallback(() => {
    historyRef.current = []; // Clear history
    hasStartedRef.current = false; // Allow initialProgress to be captured again
    initialProgressRef.current = null; // Clear saved progress
    setState({
      flowType: initialFlowType,
      stepIndex: 0,
      totalSteps: 0,
      stepId: "",
      message: "",
      loadingMessage: undefined,
      layout: "full",
      action: null,
      inputRequired: false,
      speakerName: undefined,
      speakerImageUrl: undefined,
      characterPanel: undefined,
      characterPanelCycle: undefined,
      collectedData: {},
      isComplete: false,
      isLoading: false,
      error: null,
      pendingHandoff: null,
      autoAdvance: undefined,
    });
  }, [initialFlowType]);

  /**
   * Update collected data and persist to Firestore
   * This ensures frontend-only state changes (like characterId) are saved for refresh recovery
   */
  const updateData = useCallback((updates: Record<string, unknown>) => {
    // Capture this before React runs the queued updater. start() can run in
    // the same batch; its seed must never overwrite a saved continuation.
    const wasStarted = hasStartedRef.current;
    setState((prev) => {
      const newCollectedData = { ...prev.collectedData, ...updates };

      // Persist the updated state to Firestore (non-blocking)
      // Only persist if the flow has started and is not complete.
      // Skip transient steps (auth-waiting) — see comment in updateStateFromResponse.
      if (
        wasStarted &&
        !!prev.stepId &&
        !prev.isComplete &&
        prev.stepId !== "auth-waiting" &&
        onProgressUpdateRef.current
      ) {
        onProgressUpdateRef
          .current({
            flowType: prev.flowType,
            stepIndex: prev.stepIndex,
            collectedData: sanitizeForPersistence(newCollectedData),
            updatedAt: new Date().toISOString(),
          })
          .catch((err: unknown) => {
            console.error("[useHybridFlow] Failed to save progress:", err);
          });
      }

      return {
        ...prev,
        collectedData: newCollectedData,
      };
    });
  }, []);

  /**
   * Go back to the previous step
   */
  const back = useCallback(() => {
    if (historyRef.current.length === 0) return;

    const previousState = historyRef.current.pop();
    if (previousState) {
      // Restore state but ensure loading is false
      setState({
        ...previousState,
        isLoading: false,
        error: null,
      });
    }
  }, []);

  // Auto-advance to pending handoff flow when step doesn't require input
  // This enables seamless flow transitions without requiring user to click "Next"
  // We call next() instead of switchFlow() directly to ensure the step is processed first
  // EXCEPTION: The "complete" step should NOT auto-advance - it shows the character summary
  // and requires explicit user action to continue
  useEffect(() => {
    // Keep a multi-routine plan's create state until every save finishes,
    // including retries after a partial failure.
    const smileEntries = (state.collectedData.supportDraft as {
      routineEntries?: unknown[];
    } | undefined)?.routineEntries;
    const waitingForSmileJobs =
      !!smileEntries?.length &&
      state.collectedData.jobHandlerName === "routineChecklist" &&
      (!!state.collectedData.shouldCreateJob || !!state.collectedData.jobCreationError);
    const shouldAutoAdvance =
      (state.pendingHandoff || state.autoAdvance) &&
      !waitingForSmileJobs &&
      !state.inputRequired &&
      !state.isLoading &&
      !state.error &&
      state.action?.type === "info" &&
      state.stepId !== "complete";

    if (shouldAutoAdvance) {
      console.log(
        `[useHybridFlow] Auto-advancing step ${state.stepId}${
          state.pendingHandoff ? ` to handoff flow: ${state.pendingHandoff}` : ""
        }`
      );
      next(state.autoAdvance ? "_continue" : undefined);
    }
  }, [
    state.pendingHandoff,
    state.autoAdvance,
    state.inputRequired,
    state.isLoading,
    state.action?.type,
    state.stepId,
    state.collectedData,
    next,
  ]);

  // Calculate progress percentage
  const progress =
    state.totalSteps > 0
      ? Math.round(((state.stepIndex + 1) / state.totalSteps) * 100)
      : 0;

  // Can go back if there's history
  const canGoBack = historyRef.current.length > 0;

  return {
    ...state,
    progress,
    canGoBack,
    start,
    next,
    back,
    reset,
    switchFlow,
    updateData,
  };
}
